// =============================================================================
// DeliveryNoteDetailPage — detail + lifecycle actions
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, apiPut, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { StatusPill } from '../../../components/StatusPill';
import { Can, useHasPermission } from '../../../lib/permissions';

type DnStatus = 'DRAFT' | 'IN_TRANSIT' | 'DELIVERED' | 'REJECTED' | 'RETURNED' | 'FAILED';
type FailureReason = 'NOT_HOME' | 'NO_ACCESS' | 'WRONG_ADDRESS' | 'CUSTOMER_UNREACHABLE' | 'OTHER';

interface DnDetail {
  id: string;
  number: string;
  status: DnStatus;
  scheduledDate: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  approvedAt: string | null;
  attemptNumber: number;
  failureReason: FailureReason | null;
  vehicleRef: string | null;
  receivedByName: string | null;
  customerSignatureUrl: string | null;
  conditionNotes: string | null;
  disposalCollectedQty: number;
  gpsLat: number | null;
  gpsLng: number | null;
  order: {
    id: string;
    number: string;
    deliveryAddress: string | null;
    deliveryAddressPrecise: string | null;
    customer: {
      id: string;
      name: string;
      companyName: string | null;
      phone: string;
      email: string | null;
    };
  };
  driver: { id: string; name: string } | null;
  lines: Array<{
    id: string;
    productId: string;
    qtyDispatched: number;
    qtyDelivered: number | null;
    // `tracksUnitNumbers` drives the unit picker — only imported mattresses
    // carry hand-written numbers, so only those lines get one.
    product: { sku: string; name: string; category?: { tracksUnitNumbers?: boolean } };
    stockUnits?: Array<{ id: string; unitNumber: string; status: string }>;
  }>;
}

function tone(s: DnStatus): 'neutral' | 'warning' | 'positive' | 'negative' {
  if (s === 'DELIVERED') return 'positive';
  if (s === 'IN_TRANSIT' || s === 'FAILED') return 'warning';
  if (s === 'REJECTED' || s === 'RETURNED') return 'negative';
  return 'neutral';
}

const FAILURE_LABEL: Record<FailureReason, string> = {
  NOT_HOME: 'Customer not home',
  NO_ACCESS: 'No access (gate / building)',
  WRONG_ADDRESS: 'Wrong / bad address',
  CUSTOMER_UNREACHABLE: "Couldn't reach customer",
  OTHER: 'Other',
};

export function DeliveryNoteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  // Recording which numbered piece went on the van is warehouse work.
  const canPickUnits = useHasPermission('stock.units.manage');
  const [showSign, setShowSign] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showFail, setShowFail] = useState(false);

  const detail = useQuery({
    queryKey: ['delivery-note', id],
    queryFn: () => apiGet<DnDetail>(`/delivery-notes/${id}`),
    enabled: !!id,
  });

  const dispatch = useMutation({
    mutationFn: () => apiPost(`/delivery-notes/${id}/dispatch`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-note', id] }),
  });

  // Manager/director sign-off on a signed note → mints the tax invoice + recognises
  // the sale (deferred-VAT model). Errors (e.g. balance not yet collected) surface.
  const approve = useMutation({
    mutationFn: () => apiPost(`/delivery-notes/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-note', id] }),
    onError: (e) => window.alert(getApiError(e).message),
  });

  const downloadPdf = (mode: 'download' | 'inline'): void => {
    if (!detail.data) return;
    const dn = detail.data;
    void (async () => {
      try {
        const res = await api.get(`/delivery-notes/${dn.id}/pdf`, {
          params: { mode },
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (mode === 'inline') {
          const w = window.open(url, '_blank');
          if (w) setTimeout(() => w.print(), 500);
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${dn.number}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  if (detail.isLoading)
    return (
      <PageScaffold back={{ to: '/delivery-notes', label: 'Delivery notes' }}>
        <div className="text-ink-3">Loading…</div>
      </PageScaffold>
    );
  if (!detail.data)
    return (
      <PageScaffold back={{ to: '/delivery-notes', label: 'Delivery notes' }}>
        <div className="text-ink-3">Delivery note not found.</div>
      </PageScaffold>
    );
  const dn = detail.data;
  const display = dn.order.customer.companyName ?? dn.order.customer.name;

  return (
    <PageScaffold
      back={{ to: '/delivery-notes', label: 'Delivery notes' }}
      title={dn.number}
      subtitle={
        <span className="inline-flex items-center gap-2 flex-wrap">
          <StatusPill tone={tone(dn.status)}>
            {dn.status.toLowerCase().replace('_', ' ')}
          </StatusPill>
          {dn.attemptNumber > 1 && (
            <span className="text-xs text-ink-3">attempt #{dn.attemptNumber}</span>
          )}
          <Link
            to={`/orders/${dn.order.id}`}
            className="text-xs text-accent hover:underline kc-mono"
          >
            {dn.order.number}
          </Link>
        </span>
      }
      actions={
        <>
          <Button variant="secondary" onClick={() => downloadPdf('download')}>
            Download PDF
          </Button>
          <Button variant="secondary" onClick={() => downloadPdf('inline')}>
            Print
          </Button>
          <EmailDocButton
            endpoint={`/delivery-notes/${dn.id}/email`}
            defaultEmail={dn.order.customer.email}
            title="Email delivery note"
            subtitle="Send the delivery note PDF to the customer."
          />
          {dn.status === 'DRAFT' && (
            <Can permission="delivery_notes.dispatch">
              <Button onClick={() => dispatch.mutate()} disabled={dispatch.isPending}>
                {dispatch.isPending ? 'Dispatching…' : 'Mark dispatched'}
              </Button>
            </Can>
          )}
          {dn.status === 'IN_TRANSIT' && (
            <Can permission="delivery_notes.sign">
              <Button onClick={() => setShowSign(true)}>Sign / proof of delivery</Button>
              <Button variant="ghost" onClick={() => setShowFail(true)}>
                Couldn&apos;t deliver
              </Button>
              <Button variant="ghost" onClick={() => setShowReject(true)}>
                Customer refused
              </Button>
            </Can>
          )}
          {dn.status === 'DELIVERED' && !dn.approvedAt && (
            <Can permission="delivery_notes.approve">
              <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
                {approve.isPending ? 'Approving…' : 'Approve & issue invoice'}
              </Button>
            </Can>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Lines" bodyClassName="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">SKU</th>
                  <th className="px-6 py-2 font-medium">Product</th>
                  <th className="px-6 py-2 font-medium text-right">Dispatched</th>
                  <th className="px-6 py-2 font-medium text-right">Delivered</th>
                  <th className="px-6 py-2 font-medium">Unit numbers</th>
                </tr>
              </thead>
              <tbody>
                {dn.lines.map((l) => (
                  <tr key={l.id} className="border-b border-rule-2">
                    <td className="px-6 py-3 text-xs text-ink-3 kc-mono">{l.product.sku}</td>
                    <td className="px-6 py-3 text-ink">{l.product.name}</td>
                    <td className="px-6 py-3 text-right kc-mono">{l.qtyDispatched}</td>
                    <td className="px-6 py-3 text-right kc-mono">
                      {l.qtyDelivered != null ? (
                        l.qtyDelivered
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {l.product.category?.tracksUnitNumbers ? (
                        <UnitPickerCell
                          line={l}
                          editable={dn.status === 'DRAFT' && canPickUnits}
                          deliveryNoteId={dn.id}
                          onChanged={() => void qc.invalidateQueries({ queryKey: ['delivery-note', dn.id] })}
                        />
                      ) : (
                        <span className="text-ink-3 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {dn.status === 'FAILED' && (
            <Card title="Failed delivery attempt">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-3">Reason</span>
                  <span className="text-ink">{dn.failureReason ? FAILURE_LABEL[dn.failureReason] : '—'}</span>
                </div>
                {dn.conditionNotes && (
                  <div className="pt-2 border-t border-rule">
                    <div className="text-ink-3 text-xs uppercase tracking-wide mb-1">Note</div>
                    <div className="text-ink">{dn.conditionNotes}</div>
                  </div>
                )}
                <div className="pt-2 text-xs text-ink-3">
                  The order was rescheduled for another attempt — find it again on the delivery board.
                </div>
              </div>
            </Card>
          )}

          {dn.status === 'DELIVERED' && (
            <Card title="Proof of delivery">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-3">Received by</span>
                  <span className="text-ink">{dn.receivedByName ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-3">Delivered at</span>
                  <span className="text-ink">
                    {dn.deliveredAt && new Date(dn.deliveredAt).toLocaleString('en-MU')}
                  </span>
                </div>
                {dn.disposalCollectedQty > 0 && (
                  <div className="flex justify-between">
                    <span className="text-ink-3">Old mattresses picked up</span>
                    <span className="text-accent kc-mono">{dn.disposalCollectedQty}</span>
                  </div>
                )}
                {dn.gpsLat && dn.gpsLng && (
                  <div className="flex justify-between">
                    <span className="text-ink-3">GPS</span>
                    <span className="text-ink-3 kc-mono">
                      {dn.gpsLat.toFixed(5)}, {dn.gpsLng.toFixed(5)}
                    </span>
                  </div>
                )}
                {dn.conditionNotes && (
                  <div className="pt-2 border-t border-rule">
                    <div className="text-ink-3 text-xs uppercase tracking-wide mb-1">
                      Condition notes
                    </div>
                    <div className="text-ink-2 whitespace-pre-wrap">{dn.conditionNotes}</div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card title="Customer">
            <div className="text-ink font-medium">{display}</div>
            <div className="text-sm text-ink-3 mt-0.5">{dn.order.customer.phone}</div>
            <div className="text-xs text-ink-3 mt-2 whitespace-pre-line">
              {dn.order.deliveryAddressPrecise || dn.order.deliveryAddress || '—'}
            </div>
          </Card>
          <Card title="Driver">
            <div className="text-ink">
              {dn.driver?.name ?? <span className="italic text-ink-3">unassigned</span>}
            </div>
            {dn.vehicleRef && (
              <div className="text-xs text-ink-3 mt-1">Vehicle: {dn.vehicleRef}</div>
            )}
            {dn.scheduledDate && (
              <div className="text-xs text-ink-3">
                Scheduled: {new Date(dn.scheduledDate).toLocaleDateString('en-MU')}
              </div>
            )}
            {dn.dispatchedAt && (
              <div className="text-xs text-positive">
                Out at {new Date(dn.dispatchedAt).toLocaleString('en-MU')}
              </div>
            )}
          </Card>
        </div>
      </div>

      {showSign && (
        <SignModal
          dnId={dn.id}
          lines={dn.lines}
          onClose={() => setShowSign(false)}
          onDone={() => {
            setShowSign(false);
            qc.invalidateQueries({ queryKey: ['delivery-note', id] });
          }}
        />
      )}
      {showReject && (
        <RejectModal
          dnId={dn.id}
          onClose={() => setShowReject(false)}
          onDone={() => {
            setShowReject(false);
            qc.invalidateQueries({ queryKey: ['delivery-note', id] });
          }}
        />
      )}
      {showFail && (
        <FailModal
          dnId={dn.id}
          onClose={() => setShowFail(false)}
          onDone={() => {
            setShowFail(false);
            qc.invalidateQueries({ queryKey: ['delivery-note', id] });
          }}
        />
      )}
    </PageScaffold>
  );
}

// ─── Sign modal ─────────────────────────────────────────────────────────────
function SignModal({
  dnId,
  lines,
  onClose,
  onDone,
}: {
  dnId: string;
  lines: Array<{ id: string; qtyDispatched: number; product: { name: string } }>;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [receivedByName, setReceivedByName] = useState('');
  const [conditionNotes, setConditionNotes] = useState('');
  const [disposalCollectedQty, setDisposal] = useState('0');
  const [perLine, setPerLine] = useState<Record<string, number>>(
    Object.fromEntries(lines.map((l) => [l.id, l.qtyDispatched])),
  );
  const [gpsLat, setLat] = useState<string>('');
  const [gpsLng, setLng] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  const captureGps = (): void => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLat(String(p.coords.latitude));
        setLng(String(p.coords.longitude));
      },
      (e) => setErr(`GPS unavailable: ${e.message}`),
    );
  };

  const sign = useMutation({
    mutationFn: () =>
      apiPost(`/delivery-notes/${dnId}/sign`, {
        receivedByName,
        conditionNotes: conditionNotes || undefined,
        disposalCollectedQty: Number(disposalCollectedQty) || 0,
        gpsLat: gpsLat ? Number(gpsLat) : undefined,
        gpsLng: gpsLng ? Number(gpsLng) : undefined,
        lines: Object.entries(perLine).map(([id, qty]) => ({ lineId: id, qtyDelivered: qty })),
      }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Proof of delivery"
      subtitle="Customer signs at the door. Capture condition + GPS if delivered."
      size="xl"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              sign.mutate();
            }}
            disabled={!receivedByName.trim() || sign.isPending}
          >
            {sign.isPending ? 'Confirming…' : 'Confirm delivered'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Received by (name)"
          value={receivedByName}
          onChange={(e) => setReceivedByName(e.target.value)}
          placeholder="Customer or representative name"
        />
        <div>
          <div className="text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
            Qty delivered (per line)
          </div>
          <div className="space-y-1.5">
            {lines.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-ink-2 truncate">{l.product.name}</span>
                <input
                  type="number"
                  min="0"
                  max={l.qtyDispatched}
                  step="1"
                  value={perLine[l.id] ?? 0}
                  onChange={(e) =>
                    setPerLine((cur) => ({
                      ...cur,
                      [l.id]: Math.max(0, Math.min(l.qtyDispatched, Number(e.target.value) || 0)),
                    }))
                  }
                  className="kc-input text-sm text-right w-24"
                />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
            Condition notes
          </div>
          <textarea
            value={conditionNotes}
            onChange={(e) => setConditionNotes(e.target.value)}
            placeholder="e.g. Mattress scratched at corner, customer accepted with discount discussion"
            className="kc-input min-h-[60px] w-full text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Disposal mattresses picked up"
            type="number"
            min="0"
            step="1"
            value={disposalCollectedQty}
            onChange={(e) => setDisposal(e.target.value)}
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">GPS</span>
            <div className="flex gap-2 items-center">
              <Button size="sm" variant="ghost" onClick={captureGps}>
                📍 Capture
              </Button>
              {gpsLat && (
                <span className="text-xs text-ink-3 kc-mono">
                  {Number(gpsLat).toFixed(4)}, {Number(gpsLng).toFixed(4)}
                </span>
              )}
            </div>
          </div>
        </div>
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}

// ─── Reject modal ───────────────────────────────────────────────────────────
function RejectModal({
  dnId,
  onClose,
  onDone,
}: {
  dnId: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const reject = useMutation({
    mutationFn: () => apiPost(`/delivery-notes/${dnId}/reject`, { reason }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Customer refused delivery"
      subtitle="Marks the DN rejected. You'll likely want to issue a Credit Note from the original invoice next."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              reject.mutate();
            }}
            disabled={!reason.trim() || reject.isPending}
          >
            {reject.isPending ? 'Marking…' : 'Confirm rejection'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Wrong product delivered"
        />
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}

// ─── Couldn't-deliver modal (logs a failed attempt + reschedules) ───────────
function FailModal({
  dnId,
  onClose,
  onDone,
}: {
  dnId: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [reason, setReason] = useState<FailureReason>('NOT_HOME');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const fail = useMutation({
    mutationFn: () =>
      apiPost(`/delivery-notes/${dnId}/fail`, {
        reason,
        rescheduleDate,
        note: note || undefined,
      }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  return (
    <Modal
      title="Couldn't deliver — reschedule"
      subtitle="Logs a failed attempt and moves the order to a new delivery date. No invoice is issued — the goods never changed hands."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              fail.mutate();
            }}
            disabled={!rescheduleDate || fail.isPending}
          >
            {fail.isPending ? 'Saving…' : 'Log attempt & reschedule'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs uppercase tracking-wide text-ink-3">Reason</span>
          <select
            className="kc-input"
            value={reason}
            onChange={(e) => setReason(e.target.value as FailureReason)}
          >
            {(Object.keys(FAILURE_LABEL) as FailureReason[]).map((r) => (
              <option key={r} value={r}>
                {FAILURE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <Input
          type="date"
          label="New delivery date"
          value={rescheduleDate}
          onChange={(e) => setRescheduleDate(e.target.value)}
        />
        <Input
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. called twice, no answer"
        />
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}

// ── Unit picker (imported mattresses) ────────────────────────────────────────
// The person loading the van records which numbered pieces they actually took
// off the rack. Editable only while the note is a DRAFT — once it's dispatched,
// the record of what went on it must stop moving.
//
// Deliberately non-blocking: a line can be dispatched with no units picked. The
// dispatch response returns a warning instead, because a missing number must not
// strand a van that is loaded and ready to leave.
function UnitPickerCell({
  line,
  editable,
  deliveryNoteId,
  onChanged,
}: {
  line: {
    id: string;
    productId: string;
    qtyDispatched: number;
    product: { sku: string; name: string };
    stockUnits?: Array<{ id: string; unitNumber: string; status: string }>;
  };
  editable: boolean;
  deliveryNoteId: string;
  onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const picked = line.stockUnits ?? [];

  return (
    <>
      <div className="flex items-center gap-2">
        {picked.length > 0 ? (
          <span className="kc-mono text-xs text-ink">{picked.map((u) => u.unitNumber).join(', ')}</span>
        ) : (
          <span className="text-xs text-warning">Not recorded</span>
        )}
        {editable && (
          <button type="button" className="text-xs text-accent hover:underline" onClick={() => setOpen(true)}>
            {picked.length > 0 ? 'Change' : 'Pick'}
          </button>
        )}
      </div>
      {open && (
        <UnitPickerModal
          line={line}
          deliveryNoteId={deliveryNoteId}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function UnitPickerModal({
  line,
  deliveryNoteId,
  onClose,
  onDone,
}: {
  line: {
    id: string;
    productId: string;
    qtyDispatched: number;
    product: { sku: string; name: string };
    stockUnits?: Array<{ id: string; unitNumber: string; status: string }>;
  };
  deliveryNoteId: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const alreadyPicked = line.stockUnits ?? [];
  const [selected, setSelected] = useState<string[]>(alreadyPicked.map((u) => u.id));
  const [err, setErr] = useState<string | null>(null);

  // The pickable pool: everything still on the rack for this product, plus
  // whatever is already on this line (so a re-open shows the current choice).
  const pool = useQuery({
    queryKey: ['pickable-units', line.productId],
    queryFn: () =>
      apiGet<Array<{ id: string; unitNumber: string; location: { name: string } }>>('/stock/units', {
        productId: line.productId,
        status: 'available',
      }),
  });
  // Units already on this line are DISPATCHED (not "available"), so a re-open
  // would otherwise show the current choice as unavailable and lose it.
  const options = [
    ...(pool.data ?? []),
    ...alreadyPicked
      .filter((p) => !(pool.data ?? []).some((u) => u.id === p.id))
      .map((p) => ({ id: p.id, unitNumber: p.unitNumber, location: { name: 'on this note' } })),
  ].sort((a, b) => a.unitNumber.localeCompare(b.unitNumber));

  const save = useMutation({
    mutationFn: () =>
      apiPut(`/delivery-notes/${deliveryNoteId}/lines/${line.id}/units`, { stockUnitIds: selected }),
    onSuccess: onDone,
    onError: (e: unknown) => setErr(getApiError(e)?.message ?? 'Could not save that selection.'),
  });

  const toggle = (unitId: string): void =>
    setSelected((prev) => (prev.includes(unitId) ? prev.filter((x) => x !== unitId) : [...prev, unitId]));

  return (
    <Modal title={`Unit numbers — ${line.product.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-ink-3">
          Tick the numbers physically going on the van. {line.qtyDispatched} piece
          {line.qtyDispatched === 1 ? '' : 's'} dispatched on this line.
        </p>
        {err && <div className="text-sm text-negative">{err}</div>}
        <div className="max-h-64 overflow-y-auto border border-rule-2 rounded">
          {options.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-3">
              No numbered pieces on the rack for this product.
            </div>
          )}
          {options.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-paper-2 cursor-pointer">
              <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
              <span className="kc-mono text-sm text-ink">{u.unitNumber}</span>
              <span className="text-xs text-ink-3 ml-auto">{u.location.name}</span>
            </label>
          ))}
        </div>
        <div className="text-xs text-ink-3">
          {selected.length} selected of {line.qtyDispatched} dispatched.
          {selected.length !== line.qtyDispatched && ' You can still dispatch — this is only a warning.'}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
